import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, Plus, Users, BarChart3, Download, Send, Eye,
  CheckCircle, Clock, AlertCircle, Euro, TrendingUp,
  ArrowRight, ArrowLeft, Sparkles, Shield, Zap,
  Home, Settings, LogOut, Bell, Search, Filter,
  Calendar, Receipt, PieChart, CreditCard, Folder
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { analytics } from '../lib/ga4';

// Fake data for demo
const DEMO_INVOICES = [
  { id: 1, number: 'FAC-2026-001', client: 'Boulangerie Martin', amount: 1250.00, status: 'paid', date: '15 Fév 2026' },
  { id: 2, number: 'FAC-2026-002', client: 'Restaurant Le Gourmet', amount: 3420.50, status: 'pending', date: '18 Fév 2026' },
  { id: 3, number: 'FAC-2026-003', client: 'Garage Dubois', amount: 890.00, status: 'paid', date: '20 Fév 2026' },
  { id: 4, number: 'FAC-2026-004', client: 'Salon de Coiffure Élégance', amount: 450.00, status: 'overdue', date: '10 Fév 2026' },
  { id: 5, number: 'FAC-2026-005', client: 'Cabinet Comptable Experts', amount: 2100.00, status: 'draft', date: '22 Fév 2026' },
];

const DEMO_STATS = {
  totalRevenue: 8110.50,
  invoiceCount: 5,
  paidCount: 2,
  pendingAmount: 3870.50
};

const DemoPage = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [showTooltip, setShowTooltip] = useState(true);

  // Track demo view on mount
  useEffect(() => {
    analytics.trackDemoView();
  }, []);

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    analytics.trackDemoInteraction(`view_${sectionId}`);
  };

  const statusColors = {
    paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
  };

  const statusLabels = {
    paid: 'Payée',
    pending: 'En attente',
    overdue: 'En retard',
    draft: 'Brouillon'
  };

  const menuItems = [
    { id: 'dashboard', icon: Home, label: 'Tableau de bord' },
    { id: 'invoices', icon: FileText, label: 'Factures' },
    { id: 'clients', icon: Users, label: 'Clients' },
    { id: 'stats', icon: BarChart3, label: 'Statistiques' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
      {/* Demo Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-3 px-4 text-center">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-3 flex-wrap">
          <Sparkles className="h-5 w-5" />
          <span className="font-medium">Mode Démonstration</span>
          <span className="text-blue-100">— Explorez l'interface sans créer de compte</span>
          <Link 
            to="/register"
            className="inline-flex items-center gap-1 px-4 py-2 bg-white text-blue-600 hover:bg-blue-50 font-medium rounded-md text-sm ml-2 transition-colors"
          >
            Créer mon compte gratuit
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-52px)] bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 p-4 hidden md:block">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8 px-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900 dark:text-white">SmartFacture</h1>
              <p className="text-xs text-slate-500">Démo Interactive</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleSectionChange(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                    activeSection === item.id
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Help Box */}
          <div className="mt-8 p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-2">💡 C'est quoi l'e-facturation ?</h3>
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              À partir de 2026, toutes les factures entre entreprises doivent être électroniques. SmartFacture vous met en conformité automatiquement.
            </p>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          {/* Tooltip for beginners */}
          {showTooltip && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center shrink-0">
                <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Bienvenue dans la démo !</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Ceci est une version de démonstration avec des données fictives. Cliquez sur les différents menus à gauche pour explorer toutes les fonctionnalités. Aucune donnée n'est enregistrée.
                </p>
              </div>
              <button 
                onClick={() => setShowTooltip(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
          )}

          {/* Dashboard View */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Bonjour, Entreprise Demo 👋</h2>
                  <p className="text-slate-500">Voici le résumé de votre activité</p>
                </div>
                <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/25">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle facture
                </Button>
              </div>

              {/* Stats Cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Chiffre d'affaires</p>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{DEMO_STATS.totalRevenue.toLocaleString('fr-FR')} €</p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                        <Euro className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1 text-sm text-emerald-600">
                      <TrendingUp className="h-4 w-4" />
                      <span>+12% ce mois</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Factures émises</p>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{DEMO_STATS.invoiceCount}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                        <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-slate-500">
                      Ce mois-ci
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Payées</p>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{DEMO_STATS.paidCount}</p>
                      </div>
                      <div className="w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-xl flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-emerald-600">
                      40% du total
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">En attente</p>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{DEMO_STATS.pendingAmount.toLocaleString('fr-FR')} €</p>
                      </div>
                      <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                        <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-amber-600">
                      3 factures
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Invoices */}
              <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-slate-900 dark:text-white">Dernières factures</CardTitle>
                    <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                      Voir tout
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {DEMO_INVOICES.slice(0, 4).map((invoice) => (
                      <div 
                        key={invoice.id}
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-white dark:bg-slate-700 rounded-lg flex items-center justify-center shadow-sm">
                            <FileText className="h-5 w-5 text-blue-500" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{invoice.number}</p>
                            <p className="text-sm text-slate-500">{invoice.client}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-slate-900 dark:text-white">{invoice.amount.toLocaleString('fr-FR')} €</p>
                            <p className="text-xs text-slate-500">{invoice.date}</p>
                          </div>
                          <Badge className={statusColors[invoice.status]}>
                            {statusLabels[invoice.status]}
                          </Badge>
                          <Eye className="h-5 w-5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Compliance Score */}
              <Card className="bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-900/20 dark:to-cyan-900/20 border-emerald-200 dark:border-emerald-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center shadow-lg">
                      <Shield className="h-8 w-8 text-emerald-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 dark:text-white text-lg">Score de conformité : 100%</h3>
                      <p className="text-slate-600 dark:text-slate-400">Vos factures sont 100% conformes à la réglementation e-facturation 2026</p>
                    </div>
                    <Badge className="bg-emerald-500 text-white text-sm px-3 py-1">
                      ✓ Conforme
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Invoices View */}
          {activeSection === 'invoices' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Mes Factures</h2>
                  <p className="text-slate-500">Gérez toutes vos factures en un seul endroit</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="border-slate-300">
                    <Filter className="h-4 w-4 mr-2" />
                    Filtrer
                  </Button>
                  <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
                    <Plus className="h-4 w-4 mr-2" />
                    Nouvelle facture
                  </Button>
                </div>
              </div>

              {/* Info for beginners */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Comment ça marche ?
                </h4>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• <strong>Créez une facture</strong> en cliquant sur "Nouvelle facture"</li>
                  <li>• <strong>Format Factur-X automatique</strong> : vos factures sont conformes 2026</li>
                  <li>• <strong>Envoyez par email</strong> ou téléchargez le PDF</li>
                  <li>• <strong>Suivez les paiements</strong> en temps réel</li>
                </ul>
              </div>

              {/* Invoices Table */}
              <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 dark:bg-slate-800/50">
                        <tr>
                          <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Numéro</th>
                          <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Client</th>
                          <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Date</th>
                          <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Montant</th>
                          <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Statut</th>
                          <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {DEMO_INVOICES.map((invoice) => (
                          <tr key={invoice.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="px-6 py-4">
                              <span className="font-medium text-slate-900 dark:text-white">{invoice.number}</span>
                            </td>
                            <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{invoice.client}</td>
                            <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{invoice.date}</td>
                            <td className="px-6 py-4 font-semibold text-slate-900 dark:text-white">{invoice.amount.toLocaleString('fr-FR')} €</td>
                            <td className="px-6 py-4">
                              <Badge className={statusColors[invoice.status]}>
                                {statusLabels[invoice.status]}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-400 hover:text-blue-500 transition-colors">
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-400 hover:text-emerald-500 transition-colors">
                                  <Download className="h-4 w-4" />
                                </button>
                                <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-400 hover:text-violet-500 transition-colors">
                                  <Send className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Clients View */}
          {activeSection === 'clients' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Mes Clients</h2>
                  <p className="text-slate-500">Gérez votre base de clients</p>
                </div>
                <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouveau client
                </Button>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: 'Boulangerie Martin', email: 'contact@boulangerie-martin.fr', invoices: 12, total: 15600 },
                  { name: 'Restaurant Le Gourmet', email: 'info@legourmet.fr', invoices: 8, total: 24300 },
                  { name: 'Garage Dubois', email: 'garage.dubois@email.fr', invoices: 5, total: 4500 },
                  { name: 'Salon Élégance', email: 'elegance@salon.fr', invoices: 3, total: 1350 },
                ].map((client, idx) => (
                  <Card key={idx} className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg hover:border-blue-500/30 transition-all cursor-pointer">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                          {client.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 dark:text-white truncate">{client.name}</h3>
                          <p className="text-sm text-slate-500 truncate">{client.email}</p>
                          <div className="mt-3 flex items-center gap-4 text-sm">
                            <span className="text-slate-600 dark:text-slate-400">
                              <strong className="text-slate-900 dark:text-white">{client.invoices}</strong> factures
                            </span>
                            <span className="text-emerald-600">
                              {client.total.toLocaleString('fr-FR')} €
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Stats View */}
          {activeSection === 'stats' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Statistiques</h2>
                <p className="text-slate-500">Analysez votre activité</p>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
                      <PieChart className="h-5 w-5 text-blue-500" />
                      Répartition des factures
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { label: 'Payées', value: 40, color: 'bg-emerald-500' },
                        { label: 'En attente', value: 35, color: 'bg-amber-500' },
                        { label: 'En retard', value: 15, color: 'bg-red-500' },
                        { label: 'Brouillons', value: 10, color: 'bg-slate-400' },
                      ].map((item, idx) => (
                        <div key={idx}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-600 dark:text-slate-400">{item.label}</span>
                            <span className="font-medium text-slate-900 dark:text-white">{item.value}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.value}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
                      <TrendingUp className="h-5 w-5 text-emerald-500" />
                      Chiffre d'affaires mensuel
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48 flex items-end justify-between gap-2">
                      {[65, 45, 80, 55, 90, 75, 95].map((height, idx) => (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                          <div 
                            className="w-full bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t-lg transition-all hover:from-blue-600 hover:to-cyan-500"
                            style={{ height: `${height}%` }}
                          />
                          <span className="text-xs text-slate-500">
                            {['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil'][idx]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* CTA Footer */}
      <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 py-6 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            Convaincu ? Créez votre compte gratuitement
          </h3>
          <p className="text-slate-500 mb-4">
            7 jours d'essai gratuit • Aucune carte bancaire requise • Annulation à tout moment
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link 
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-medium rounded-lg shadow-lg shadow-blue-500/25 transition-all"
            >
              Commencer gratuitement
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link 
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium rounded-lg transition-all"
            >
              <ArrowLeft className="h-5 w-5" />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
